/**
 * Which surface collects a product's source files, and which products therefore
 * need the editing configuration page.
 *
 * The file a product is MADE from is configuration, so it belongs in the
 * configuration step with everything else that is chosen FOR the product. Most
 * upload-taking products already have a page that can hold it — a floor plan has
 * `floor_plan_config`, staging has `staging`. The editing family has none: on the
 * dev catalogue, thirteen upload-taking products (rendering, photo editing,
 * slideshow, timelapse) announce no `requiredConfiguration` at all, and the one
 * that does — AI editing — announces `AI_EDITING`, whose settings are collected
 * in the product drawer beside the price they apply to, not on a step.
 *
 * So the page is derived here rather than waited for: a product that takes input
 * images and has no step of its own gets `editing_config`. `ConfigurationFlowId`
 * is documented as an FE-side mapping, and deriving it needs no backend change.
 *
 * The derivation is also what makes the requirement decidable. Every product
 * carrying `editing_config` takes input images BY CONSTRUCTION, so an empty file
 * list is always wrong for this flow — unlike `floor_plan_config`, where
 * `MOBILE_APPLICATION_FLOOR_PLAN` is `NONE` and legitimately carries nothing.
 */

import type { ConfigurationFlowId, InputImagePolicyV2 } from 'models/purchaseFlowRevamp'

/**
 * Flows that own no page of their own, because another flow's page collects them.
 *
 * They must never become a configuration TAB: the step's Proceed gate needs every
 * tuple confirmed by the flow's own module (`markConfigSaved`), and a flow with no
 * module can never be confirmed — so admitting one does not add a screen, it traps
 * the client on the step with nothing to fill in.
 *
 * `ai_editing` is the case. The catalogue announces it on AI-editing products, and
 * its sky settings are rendered inside the `editing_config` page beside the visuals
 * they apply to.
 */
export const NESTED_FLOWS: ReadonlySet<ConfigurationFlowId> = new Set<ConfigurationFlowId>([
  'ai_editing',
])

/** The configuration page that exists to collect a product's source files. */
export const SOURCE_UPLOAD_FLOW: ConfigurationFlowId = 'editing_config'

/**
 * Appends `editing_config` when the product takes input images and no flow that
 * owns a configuration step already collects them.
 *
 * @param flows             The product's own flows, as the catalogue announced them.
 * @param inputImagePolicy  The product's `inputImagePolicy`.
 */
export function withSourceUploadFlow(
  flows: readonly ConfigurationFlowId[],
  inputImagePolicy: InputImagePolicyV2 | undefined
): ConfigurationFlowId[] {
  const out = [...flows]
  if (!inputImagePolicy || inputImagePolicy === 'NONE') return out
  if (out.some((flow) => !NESTED_FLOWS.has(flow))) return out
  out.push(SOURCE_UPLOAD_FLOW)
  return out
}

/**
 * Flows whose own page already collects the product's source files.
 *
 * `floor_plan_config` has carried its dropzone since the floor-plan half of the
 * move; `editing_config` exists for no other reason. Every OTHER flow with a
 * page of its own — staging, floor-plan certification — configures a product
 * that is produced FROM visuals it never asks for, which is the gap this set
 * defines: a flow outside it gets the shared upload injected above its module.
 *
 * Membership is about who OWNS the dropzone, not about who needs one. Adding a
 * flow here without giving it an upload is how a product silently goes back to
 * ordering nothing to work from.
 */
export const FLOWS_WITH_OWN_SOURCE_UPLOAD: ReadonlySet<ConfigurationFlowId> =
  new Set<ConfigurationFlowId>([
    'floor_plan_config',
    SOURCE_UPLOAD_FLOW,
  ])

/**
 * Whether a line must carry at least one source file to be orderable.
 *
 * The catalogue decides, never the flow: `2D_FLOOR_PLAN_EDITING` is `UNLIMITED`
 * and `MOBILE_APPLICATION_FLOOR_PLAN` is `NONE` on the same `floor_plan_config`
 * flow, and staging is `EXACTLY_COUNT` on all four of its variants. So the
 * question is asked of `inputImagePolicy` alone.
 */
export const requiresSourceFiles = (policy: InputImagePolicyV2 | undefined): boolean =>
  !!policy && policy !== 'NONE'

/**
 * Whether the shared upload surface must be rendered above this flow's module.
 *
 * True for a flow that needs files and does not collect them itself. This is
 * what puts the dropzone on staging and floor-plan certification without naming
 * either: a future product announcing any flow at all gets one the moment its
 * catalogue entry says it takes input images.
 *
 * @param flowId  The flow whose page is being rendered.
 * @param policy  The bought variant's `inputImagePolicy`.
 */
export const needsSourceUploadSurface = (
  flowId: ConfigurationFlowId,
  policy: InputImagePolicyV2 | undefined
): boolean => requiresSourceFiles(policy) && !FLOWS_WITH_OWN_SOURCE_UPLOAD.has(flowId)

/**
 * Whether one produced item is bought per uploaded visual.
 *
 * `EXACTLY_COUNT` is the rule the order validator applies and the reason
 * `buildInputImages` drops a whole set whose count disagrees with the quantity.
 * `UNLIMITED` products buy one item however many files feed them.
 */
export const isOneItemPerVisual = (policy: InputImagePolicyV2 | undefined): boolean =>
  policy === 'EXACTLY_COUNT'
